import React, { useState } from 'react';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { Clock, Clipboard, TriangleAlert, SquareCheckBig, Banknote, Phone, Building2, User, Users } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';

const SimpleTramiteForm = ({ tramite, user, onSubmit, onCancel }) => {
  const { success, error } = useNotifications();

  const [formData, setFormData] = useState({
    // Campos condicionales según si está logueado
    ...(user ? {} : {
      nombre_completo: '',
      telefono: '',
      email: '',
    }),
    // Campos siempre presentes
    beneficiario_tipo: 'para_mi', // para_mi, para_empresa, para_tercero
    nombre_beneficiario: '',
    confirmacion_documentos: false
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  /**
   * Valida el formulario según el estado de autenticación
   */
  const validateForm = () => {
    const newErrors = {};

    // Si NO está logueado, validar datos básicos
    if (!user) {
      if (!formData.nombre_completo?.trim()) {
        newErrors.nombre_completo = 'Nombre completo es requerido';
      } else if (formData.nombre_completo.trim().length < 2) {
        newErrors.nombre_completo = 'Nombre debe tener al menos 2 caracteres';
      }

      if (!formData.telefono?.trim()) {
        newErrors.telefono = 'Teléfono es requerido';
      } else if (!/^[0-9]{10}$/.test(formData.telefono)) {
        newErrors.telefono = 'Teléfono debe tener 10 dígitos';
      }

      if (!formData.email?.trim()) {
        newErrors.email = 'Email es requerido';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Email no válido';
      }
    }

    // Validar beneficiario si es para tercero
    if (formData.beneficiario_tipo === 'para_tercero') {
      if (!formData.nombre_beneficiario?.trim()) {
        newErrors.nombre_beneficiario = 'Nombre del beneficiario es requerido';
      }
    }

    // Validar confirmación de documentos
    /*if (!formData.confirmacion_documentos) {
      newErrors.confirmacion_documentos = 'Debes confirmar que tienes los documentos disponibles';
    }*/

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Maneja cambios en los campos
   */
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
      success('Error enviando solicitud. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header con navegación */}
        <div className="mb-8">
          <button
            onClick={onCancel}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4 font-medium"
          >
            ← Volver a seleccionar trámite
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                🚀 Solicitar: {tramite.name}
              </h1>
              <p className="text-gray-600 text-lg">
                {tramite.description}
              </p>
            </div>

            {/* Información transparente del trámite */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-green-700 font-medium gap-2">
                    <Banknote /> Precio:
                  </span>
                  <span className="font-bold text-green-800 text-lg">
                    {tramite.price_type === 'fijo'
                      ? `$${tramite.fixed_price} MXN`
                      : 'Cotización requerida'}
                  </span>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-blue-700 font-medium"><Clock className="mr-1" /> Tiempo estimado:</span>
                  <span className="font-bold text-blue-800 text-lg">
                    {tramite.estimated_time_days} días hábiles
                  </span>
                </div>
              </div>
            </div>

            {/* DOCUMENTOS REQUERIDOS - PROMINENTEMENTE VISIBLES */}
            <div className="mb-8 p-6 bg-yellow-50 border-2 border-yellow-300 rounded-xl">
              <h3 className="text-xl font-bold text-yellow-900 mb-4 flex row items-center">
                <Clipboard /> Documentos que necesitarás tener listos:
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {tramite.required_documents?.map((doc, index) => (
                  <div key={index} className="flex items-start bg-white p-3 rounded-lg">
                    <span className="w-3 h-3 bg-yellow-600 rounded-full mr-3 mt-1 flex-shrink-0"></span>
                    <span className="text-sm text-gray-800 font-medium">{doc}</span>
                  </div>
                )) || (
                    <div className="col-span-2 text-center text-gray-500">
                      No hay documentos especificados para este trámite
                    </div>
                  )}
              </div>

              <div className="mt-4 p-4 bg-yellow-100 rounded-lg">
                <div className="flex items-start">
                  <span className="text-2xl mr-3"><TriangleAlert /></span>
                  <div>
                    <p className="text-yellow-800 font-semibold mb-1">
                      Importante: Revisa esta lista antes de continuar
                    </p>
                    <p className="text-yellow-700 text-sm">
                      Necesitarás tener todos estos documentos disponibles para completar tu trámite exitosamente.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FORMULARIO SIMPLE */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex row items-center">
            <Clipboard className='mr-1' />Información de contacto
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* CAMPOS SOLO PARA USUARIOS NO LOGUEADOS */}
            {!user && (
              <>
                {/* Nombre Completo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre Completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nombre_completo}
                    onChange={(e) => handleChange('nombre_completo', e.target.value)}
                    className={`w-full p-4 border rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${errors.nombre_completo ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Ingresa tu nombre completo"
                    required
                  />
                  {errors.nombre_completo && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <span className="mr-1"><TriangleAlert /></span>
                      {errors.nombre_completo}
                    </p>
                  )}
                </div>

                {/* Teléfono */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value.replace(/\D/g, '').substring(0, 10))}
                    className={`w-full p-4 border rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${errors.telefono ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="5512345678"
                    required
                  />
                  {errors.telefono && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <span className="mr-1"><TriangleAlert /></span>
                      {errors.telefono}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Correo Electrónico <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`w-full p-4 border rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="correo@ejemplo.com"
                    required
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <span className="mr-1"><TriangleAlert /></span>
                      {errors.email}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* SELECTOR DE BENEFICIARIO - SIEMPRE PRESENTE */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                ¿Para quién es este trámite? <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                <label className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="beneficiario_tipo"
                    value="para_mi"
                    checked={formData.beneficiario_tipo === 'para_mi'}
                    onChange={(e) => handleChange('beneficiario_tipo', e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900 flex row items-center gap-2">
                    <User />Para mí {user ? `(${user.name})` : ''}
                  </span>
                </label>

                <label className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="beneficiario_tipo"
                    value="para_empresa"
                    checked={formData.beneficiario_tipo === 'para_empresa'}
                    onChange={(e) => handleChange('beneficiario_tipo', e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900 flex row items-center gap-2">
                    <Building2 /> Para mi empresa
                  </span>
                </label>

                <label className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="beneficiario_tipo"
                    value="para_tercero"
                    checked={formData.beneficiario_tipo === 'para_tercero'}
                    onChange={(e) => handleChange('beneficiario_tipo', e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900 flex row items-center gap-2">
                    <Users /> Para un tercero
                  </span>
                </label>
              </div>
            </div>

            {/* CAMPO CONDICIONAL PARA TERCERO */}
            {formData.beneficiario_tipo === 'para_tercero' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Beneficiario <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nombre_beneficiario}
                  onChange={(e) => handleChange('nombre_beneficiario', e.target.value)}
                  className={`w-full p-4 border rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${errors.nombre_beneficiario ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  placeholder="Nombre completo del beneficiario"
                  required
                />
                {errors.nombre_beneficiario && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <span className="mr-1"><TriangleAlert /></span>
                    {errors.nombre_beneficiario}
                  </p>
                )}
              </div>
            )}

            {/* CHECKBOX OBLIGATORIO DE CONFIRMACIÓN
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <input
                  type="checkbox"
                  id="confirmacion_documentos"
                  checked={formData.confirmacion_documentos}
                  onChange={(e) => handleChange('confirmacion_documentos', e.target.checked)}
                  className={`mt-1 w-5 h-5 text-blue-600 border-2 rounded focus:ring-blue-500 ${errors.confirmacion_documentos ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                />
                <label htmlFor="confirmacion_documentos" className="text-sm text-blue-900 font-medium leading-relaxed">
                  <strong>Confirmo que he revisado la lista de documentos requeridos y los tengo disponibles</strong> para completar este trámite exitosamente.
                </label>
              </div>

              {errors.confirmacion_documentos && (
                <p className="text-red-500 text-sm mt-3 flex items-center pl-9">
                  <span className="mr-1"><TriangleAlert /></span>
                  {errors.confirmacion_documentos}
                </p>
              )}
            </div> */}

            {/* Botones de acción */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
              >
                ← Cancelar
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Procesando...
                  </>
                ) : (
                  <>
                    <SquareCheckBig className='mr-2' /> Confirmar Solicitud
                  </>
                )}
              </button>
            </div>

            {/* Información adicional */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2 flex row items-center gap-2">
                <Phone /> ¿Qué sigue después de confirmar?
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Se abrirá un chat automático con nuestro equipo</li>
                <li>• Te guiaremos paso a paso en la subida de documentos</li>
                <li>• Podrás hacer preguntas durante todo el proceso</li>
                <li>• Recibirás una cotización una vez revisados tus documentos</li>
              </ul>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SimpleTramiteForm;